import { useCallback, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useTenant } from "../auth/TenantContext";
import { msalInstance } from "../auth/AuthProvider";

const useLocalTokenBridge = import.meta.env.VITE_USE_LOCAL_TOKEN_BRIDGE === "true";
const localTokenBridgeUrl = import.meta.env.VITE_LOCAL_TOKEN_BRIDGE_URL || "http://localhost:3031/token";

export interface WorkspaceInfo {
    /** Full ARM resource ID */
    id: string;
    /** Display name (last path segment) */
    name: string;
}

/** Extract subscription ID from an ARM resource ID. */
function parseSubscriptionId(resourceId: string): string | null {
    const match = resourceId.match(/\/subscriptions\/([^/]+)/i);
    return match ? match[1] : null;
}

/** Normalize a resource ID for comparison (lowercase, strip trailing slashes). */
function normalizeId(id: string): string {
    return id.replace(/\/+$/, "").toLowerCase();
}

/**
 * Hook that lists Playwright workspace resources in the same subscription
 * as the given resourceId. Gracefully returns an empty list on failure.
 */
export function usePlaywrightWorkspaces(resourceId: string) {
    const { instance, accounts } = useMsal();
    const { selectedTenantId } = useTenant();
    const [workspaces, setWorkspaces] = useState<WorkspaceInfo[]>([]);
    const [loading, setLoading] = useState(false);

    const acquireArmToken = useCallback(async (): Promise<string | null> => {
        try {
            if (useLocalTokenBridge) {
                const bridgeBase = localTokenBridgeUrl.replace(/\/token$/, "");
                const res = await fetch(`${bridgeBase}/arm-token`);
                if (!res.ok) return null;
                const body = (await res.json()) as { accessToken?: string };
                return body.accessToken ?? null;
            }

            const account = instance.getActiveAccount() ?? accounts[0];
            if (!account || !selectedTenantId) return null;

            const r = await msalInstance.acquireTokenSilent({
                scopes: ["https://management.azure.com/.default"],
                authority: `https://login.microsoftonline.com/${selectedTenantId}`,
                account,
            });
            return r.accessToken;
        } catch {
            return null;
        }
    }, [instance, accounts, selectedTenantId]);

    useEffect(() => {
        if (!resourceId) {
            setWorkspaces([]);
            return;
        }

        const subscriptionId = parseSubscriptionId(resourceId);
        if (!subscriptionId) {
            setWorkspaces([]);
            return;
        }

        let cancelled = false;
        setLoading(true);

        void (async () => {
            const token = await acquireArmToken();
            if (cancelled || !token) {
                if (!cancelled) setLoading(false);
                return;
            }

            try {
                const url =
                    `https://management.azure.com/subscriptions/${encodeURIComponent(subscriptionId)}` +
                    `/resources?$filter=resourceType eq 'Microsoft.LoadTestService/playwrightWorkspaces'&api-version=2021-04-01`;

                const res = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (cancelled) return;

                if (!res.ok) {
                    setWorkspaces([]);
                    return;
                }

                const body = (await res.json()) as { value?: { id: string; name: string }[] };
                if (cancelled) return;

                const items: WorkspaceInfo[] = (body.value ?? []).map(r => ({
                    id: r.id,
                    name: r.name,
                }));

                // Ensure the current workspace appears even if the ARM call didn't return it
                const currentNorm = normalizeId(resourceId);
                if (!items.some(w => normalizeId(w.id) === currentNorm)) {
                    const segments = resourceId.replace(/\/+$/, "").split("/");
                    items.unshift({ id: resourceId, name: segments[segments.length - 1] || "Workspace" });
                }

                setWorkspaces(items);
            } catch {
                if (!cancelled) setWorkspaces([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [resourceId, acquireArmToken]);

    return { workspaces, loading };
}
